<script setup lang="ts">
import { useListInternalEmployers } from '@swipe4work/api-orval'
import type { InternalDepartment } from '@swipe4work/models-orval'

type ValueField = 'id' | 'externalId'
type EmployerValue = number | string | null

const props = withDefaults(defineProps<{
  modelValue?: EmployerValue
  inputId?: string
  invalid?: boolean
  showClear?: boolean
  placeholder?: string
  valueField?: ValueField
  disabled?: boolean
  /**
   * Restrict the selectable employees to one or more departments (based on their internal roles).
   * Leave unset to show every internal employee.
   */
  department?: InternalDepartment | InternalDepartment[]
  /**
   * When the department filter matches no employees, fall back to showing every internal employee
   * instead of an empty select.
   */
  fallbackToAll?: boolean
}>(), {
  modelValue: null,
  showClear: true,
  placeholder: 'Selecteer medewerker',
  valueField: 'id',
  disabled: false,
  fallbackToAll: false,
})

const emit = defineEmits<{
  (e: 'update:modelValue', v: EmployerValue): void
}>()

const departmentSet = computed<Set<InternalDepartment> | null>(() => {
  if (props.department == null) return null

  return new Set(Array.isArray(props.department) ? props.department : [ props.department ])
})

/*
 * Fetch every internal employee once (shared cache across all selects) and filter by department
 * client-side, so we can transparently fall back to the full list when the filter is empty.
 */
const { data, isPending } = useListInternalEmployers({ limit: 200 }, { query: { staleTime: 5 * 60 * 1000 } })

const allEmployees = computed(() => data.value?.items ?? [])

const filteredEmployees = computed(() => {
  if (!departmentSet.value) return allEmployees.value

  return allEmployees.value.filter(employee =>
    employee.roles.some(role => departmentSet.value!.has(role.department)))
})

const effectiveEmployees = computed(() =>
  props.fallbackToAll && filteredEmployees.value.length === 0 ? allEmployees.value : filteredEmployees.value)

const options = computed(() =>
  effectiveEmployees.value.map(employee => ({
    id: employee.internalEmployerId,
    externalId: employee.externalId,
    label: employee.displayName ?? employee.email,
    email: employee.email,
    department: [ ...new Set(employee.roles.map(role => role.department)) ].join(', '),
  })),
)

const optionValue = computed(() => (props.valueField === 'externalId' ? 'externalId' : 'id'))

const selected = computed({
  get: () => props.modelValue,
  set: v => emit('update:modelValue', v),
})
</script>

<template>
  <Select
    v-model="selected"
    :input-id="inputId"
    :options="options"
    option-label="label"
    :option-value="optionValue"
    :invalid="invalid"
    :loading="isPending"
    :disabled="disabled"
    :placeholder="placeholder"
    :show-clear="showClear"
    filter
    filter-placeholder="Zoek medewerker"
    class="w-full"
  >
    <template #option="{ option }">
      <div class="flex flex-col">
        <span class="text-sm">{{ option.label }}</span>
        <span v-if="option.department" class="text-xs text-surface-400">{{ option.department }}</span>
      </div>
    </template>
  </Select>
</template>
